import { SrcInfoContainer } from '@nile/banyan/src/Utils/style';
import {
  AppContext,
  Loader,
  toast,
  getTranslatedText,
  useIntl,
  useActivityTracker,
  TabContent
} from '@nile/components';
import { PlanItemContainer } from '@nile/components/src/Cards';
import {
  AccountClient,
  UPGRADE_SUBSCRIPTION_STATUS,
  CREATE_PURCHASE_SUBSCRIPTION_CONFIGURATION,
  GET_ESTIMATE_FOR_LINEITEM,
  GET_ESTIMATE_FOR_ALL_LINEITEMS
} from '@nile/graphql';
import {
  TextStyle,
  Button,
  ButtonGroup,
  DisplayText
} from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import {
  planDescriptionKeyMap,
  manageList
} from '@nile/banyan/src/Utils/pricingHelper';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { isEmpty, sortBy } from 'lodash';

export const PricingSummaryCardWrapper = styled.div`
  .Polaris-TextField {
    width: 50px;
    border: 1px solid #92979b;
  }
  .sp-arnd {
    justify-content: space-around;
  }
  .addLink {
    cursor: pointer;
    color: #011fff;
  }
  .plan-type {
    width: 30%;
  }
  .plan-inp {
    width: 15%;
    &.opacity {
      opacity: 50%;
    }
  }
  .plan-calc {
    width: 15%;
    text-align: center;
  }
  .plan-total {
    width: 15%;
    text-align: end;
  }
  .plan-total-desc {
    width: 25%;
    text-align: left;
    padding-left: 20px;
  }

  .number-holder {
    border: 1px solid #dadada;
    border-radius: 4px;
    display: flex;
    justify-content: space-around;
    padding: 4px;
    align-items: center;
    line-height: 1;
    .action-btn {
      font-size: 24px;
      font-weight: 200;
      cursor: pointer;
      user-select: none;
      flex: 1 1;
      text-align: center;
    }
    .cnt {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1 1;
      text-align: center;
    }
  }
  .bill-total-holder {
    font-size: 14px;
    background: #f6faff;
    border-radius: 10px;
    .greenText {
      color: #59b400;
    }
    min-height: 60px;
  }
  .billed-amount-holder {
    font-size: 20px;
    background: #f6faff;
    border-radius: 10px;
    .billing-wrapper {
      padding: 20px 0;
      display: flex;
    }
  }
  .left-billing-section {
    width: 60%;
    text-align: right;
    padding-right: 30px;
  }
  .right-billing-section {
    width: 15%;
    text-align: end;
  }
  .btn-switcher {
    .isActivated {
      border: 1px solid black;
    }
  }
`;

const InfoContainer = styled(SrcInfoContainer)`
  .billing-wrapper {
    color: green;
  }
`;

const PricingSummaryCard = (props) => {
  const {
    tenure,
    setTenure,
    selectedPlan,
    handleManageModal,
    currency,
    locale
  } = props;

  const intl = useIntl();

  const [intitalEstimateLoading, setIntitalEstimateLoading] = useState(false);

  const [isEstimating, setIsEstimating] = useState(false);

  const [changeTimeout, setChangeTimeout] = useState(null);

  const [planEstimate, setPlanEstimate] = useState({});
  const [licenseCount, setLicenseCount] = useState({});
  const [actualLicenseCount, setActualLicenseCount] = useState({});
  const [minimumLicenseCount, setMinimumLicenseCount] = useState({});
  const [offeringEntity, setOfferingEntity] = useState([]);

  const app = useContext(AppContext);
  const tabContext = useContext(TabContent);

  const { user, getUser } = app;

  const getEstimatedPriceForLineItem = (data = null) => {
    setIsEstimating(true);
    const estimateData = data || planEstimate;
    AccountClient.query({
      query: GET_ESTIMATE_FOR_ALL_LINEITEMS,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          plan: {
            planPrice: { id: estimateData?.plan?.planPrice?.id },
            quantity: licenseCount.TECHNICIAN
          },
          addons: estimateData?.addons?.map((el) => ({
            addonPrice: { id: el?.addonPrice?.id },
            quantity: licenseCount[el?.addonPrice?.chargeBeeAddonPriceId] || 0
          }))
        }
      }
    })
      .then((res) => {
        const quantity = {};
        if (res?.data?.getProductSubscriptionEstimateForLineItems) {
          const result = res.data.getProductSubscriptionEstimateForLineItems;
          const termResult = result?.termInvoiceEstimate;
          setPlanEstimate(termResult);
          setOfferingEntity(result?.offeringEntities);
          quantity.TECHNICIAN = termResult?.plan?.quantity;
          if (!isEmpty(termResult?.addons)) {
            sortBy(termResult?.addons, (obj) => obj?.addonPrice?.id)?.map(
              (el) => {
                quantity[el?.addonPrice?.chargeBeeAddonPriceId] = el?.quantity;
              }
            );
          }
          setLicenseCount((prevState) => ({ ...prevState, ...quantity }));
          setActualLicenseCount((prevState) => ({ ...prevState, ...quantity }));
        }
      })
      .finally(() => setIsEstimating(false));
  };

  const getEstimateForExistingLineItem = (tenureChange = false) => {
    if (tenureChange) {
      setIsEstimating(true);
    } else {
      setIntitalEstimateLoading(true);
    }
    const planPriceId = selectedPlan?.planPrices?.find(
      (prPlan) =>
        prPlan?.currency?.currencyCode === currency &&
        prPlan?.duration === tenure &&
        prPlan?.durationValue === 1
    );
    AccountClient.query({
      query: GET_ESTIMATE_FOR_LINEITEM,
      fetchPolicy: 'network-only',
      variables: {
        input: { id: planPriceId?.id }
      }
    })
      .then((res) => {
        const quantity = {};
        const minimumQuantity = {};
        if (res?.data?.getProductSubscriptionEstimateForExistingLineItems) {
          const result =
            res.data.getProductSubscriptionEstimateForExistingLineItems;
          const termResult = result?.termInvoiceEstimate;
          setPlanEstimate(termResult);
          setOfferingEntity(result?.offeringEntities);
          if (!tenureChange) {
            quantity.TECHNICIAN = termResult?.plan?.quantity;
            minimumQuantity.TECHNICIAN = termResult?.plan?.minimumQuantity;
            if (!isEmpty(termResult?.addons)) {
              sortBy(termResult?.addons, (obj) => obj?.addonPrice?.id)?.map(
                (el) => {
                  quantity[el?.addonPrice?.chargeBeeAddonPriceId] =
                    el?.quantity;
                  minimumQuantity[el?.addonPrice?.chargeBeeAddonPriceId] =
                    el?.minimumQuantity < 0 ? 0 : el?.minimumQuantity;
                }
              );
            }
            setActualLicenseCount((prevState) => ({
              ...prevState,
              ...quantity
            }));
            setLicenseCount((prevState) => ({ ...prevState, ...quantity }));
            setMinimumLicenseCount((prevState) => ({
              ...prevState,
              ...minimumQuantity
            }));
          } else {
            getEstimatedPriceForLineItem(termResult);
          }
        }
      })
      .finally(() => {
        if (!tenureChange) {
          setIntitalEstimateLoading(false);
        }
      });
  };

  const onSoloPlanHandler = () => {
    const userMeta = {
      email: user.email,
      name: user.name,
      userId: user.userId,
      subscriptionStatus: user.subscriptionStatus
    };
    window.Intercom('trackEvent', 'solo-plan', userMeta);
  };

  const onValueChange = () => {
    if (!isEmpty(planEstimate) && !intitalEstimateLoading) {
      setChangeTimeout(
        setTimeout(function() {
          getEstimatedPriceForLineItem();
        }, 1000)
      );
      if (changeTimeout) {
        clearTimeout(changeTimeout);
      }
    }
  };

  const decrementHandler = (type) => {
    setLicenseCount((prevState) => ({
      ...prevState,
      [type]: prevState[type] - 1
    }));
  };

  const incrementHandler = (type) => {
    setLicenseCount((prevState) => ({
      ...prevState,
      [type]: prevState[type] + 1
    }));
  };

  const getUpdatedUser = async () => {
    await getUser();
  };

  const openCheckout = () => {
    const chargebeeInstance = window.Chargebee.getInstance();
    chargebeeInstance.openCheckout({
      hostedPage: function() {
        return AccountClient.mutate({
          mutation: CREATE_PURCHASE_SUBSCRIPTION_CONFIGURATION,
          variables: {
            input: {
              chargeBeePlanPriceId:
                planEstimate?.plan?.planPrice?.chargeBeePlanPriceId,
              addons: !isEmpty(planEstimate?.addons)
                ? planEstimate?.addons
                    .map((el) =>
                      licenseCount[el?.addonPrice?.chargeBeeAddonPriceId]
                        ? {
                            chargeBeeAddonPriceId:
                              el?.addonPrice?.chargeBeeAddonPriceId,
                            quantity:
                              licenseCount[
                                el?.addonPrice?.chargeBeeAddonPriceId
                              ]
                          }
                        : null
                    )
                    .filter((el) => el)
                : [],
              quantity: licenseCount?.TECHNICIAN
            }
          }
        }).then((res) => {
          if (res?.data?.createPurchaseSubscriptionConfiguration) {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'C. proceed to payment btn new buy',
                subscription:
                  planEstimate?.plan?.planPrice?.chargeBeePlanPriceId,
                techCount: licenseCount?.TECHNICIAN
              }
            });
            return res.data.createPurchaseSubscriptionConfiguration;
          }
        });
      },
      success: function() {
        AccountClient.mutate({
          mutation: UPGRADE_SUBSCRIPTION_STATUS,
          variables: {
            input: {
              status: 'SUCCESS'
            }
          }
        }).then((res) => {
          if (res?.data?.updateSubscriptionPurchaseStatus) {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'D. new payment conformation',
                subscription:
                  planEstimate?.plan?.planPrice?.chargeBeePlanPriceId,
                techCount: licenseCount?.TECHNICIAN
              }
            });
            getUpdatedUser();
            toast('success', 'Payment successful');
            chargebeeInstance.closeAll();
            window.location.reload();
          } else {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'Purchase plan payment inprogress',
                subscription:
                  planEstimate?.plan?.planPrice?.chargeBeePlanPriceId,
                techCount: licenseCount?.TECHNICIAN
              }
            });
            chargebeeInstance.closeAll();
            window.location.reload();
          }
        });
      },
      close: function() {
        AccountClient.mutate({
          mutation: UPGRADE_SUBSCRIPTION_STATUS,
          variables: {
            input: {
              status: 'CANCEL'
            }
          }
        }).then((res) => {
          if (res?.data?.updateSubscriptionPurchaseStatus) {
            useActivityTracker({
              event: 'Inproduct purchase',
              data: {
                source: 'E. Payment cancelled',
                subscription:
                  planEstimate?.plan?.planPrice?.chargeBeePlanPriceId,
                techCount: licenseCount?.TECHNICIAN
              }
            });
          }
          chargebeeInstance.closeAll();
        });
      }
    });
  };

  useEffect(() => {
    onValueChange();
  }, [JSON.stringify(licenseCount)]);

  useEffect(() => {
    if (!isEmpty(planEstimate) && !intitalEstimateLoading) {
      setChangeTimeout(
        setTimeout(function() {
          getEstimateForExistingLineItem(true);
        }, 200)
      );
      if (changeTimeout) {
        clearTimeout(changeTimeout);
      }
    }
  }, [tenure]);

  useEffect(() => {
    getEstimateForExistingLineItem();
  }, []);

  if (intitalEstimateLoading) {
    return (
      <div className="fx fh fw">
        <Loader />
      </div>
    );
  }

  return (
    <PricingSummaryCardWrapper className="full-width">
      <InfoContainer>
        <div className="full-width">
          <div className="fx-sb flex actr f11 mt-30">
            <div>
              <p className="name">{selectedPlan?.planName}</p>
              <p className="excerpt">
                <TextStyle variation="subdued">
                  {selectedPlan?.chargeBeePlanId
                    ? getTranslatedText(
                        intl,
                        planDescriptionKeyMap[selectedPlan?.chargeBeePlanId]
                      )
                    : ''}
                </TextStyle>
              </p>
              <span className="italic">
                <DisplayText>
                  <TextStyle>
                    Price for
                    <span className="mlr5">
                      <TextStyle weight="Bold">
                        {selectedPlan?.planName}
                      </TextStyle>
                    </span>
                    subscription on
                    <span className="mlr5">
                      <TextStyle weight="Bold">
                        {tenure === 'MONTHLY' ? 'monthly' : 'annual'}
                      </TextStyle>
                    </span>
                  </TextStyle>
                </DisplayText>
              </span>
              <div className="line" />
            </div>
            <div className="flex jend btn-switcher">
              <ButtonGroup segmented>
                <Button
                  onClick={() => setTenure('MONTHLY')}
                  outline={tenure === 'MONTHLY'}
                >
                  <FormattedMessage id="app.pricing.tenure.monthly" />
                </Button>
                <Button
                  onClick={() => setTenure('YEARLY')}
                  outline={tenure === 'YEARLY'}
                >
                  <FormattedMessage id="app.pricing.tenure.yearly" />
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <PlanItemContainer
            selectedPlan={selectedPlan}
            type="TECHNICIAN"
            decrementHandler={decrementHandler}
            incrementHandler={incrementHandler}
            tenure={tenure}
            currency={currency}
            handleManageModal={handleManageModal}
            showManageButton
            locale={locale}
            planEstimate={planEstimate}
            includeAddon={!isEmpty(planEstimate?.addons)}
            licenseCount={licenseCount}
            actualLicenseCount={actualLicenseCount}
            minimumLicenseCount={minimumLicenseCount}
          />
          {sortBy(planEstimate?.addons, (obj) => obj?.addonPrice?.id)?.map(
            (addon) => (
              <PlanItemContainer
                selectedPlan={selectedPlan}
                type={addon?.addonPrice?.chargeBeeAddonPriceId}
                decrementHandler={decrementHandler}
                incrementHandler={incrementHandler}
                tenure={tenure}
                showManageButton={manageList.includes(
                  addon?.addon?.offeringEntityType
                )}
                handleManageModal={handleManageModal}
                currency={currency}
                locale={locale}
                addon={addon}
                includeAddon
                isAddon
                licenseCount={licenseCount}
                actualLicenseCount={actualLicenseCount}
                minimumLicenseCount={minimumLicenseCount}
                monitorCount={offeringEntity.find(
                  (el) =>
                    el?.offeringEntityType === addon?.addon?.offeringEntityType
                )}
              />
            )
          )}

          <div className="mt-50 billed-amount-holder">
            {isEstimating ? (
              <div className="fx actr">
                <Loader />
              </div>
            ) : (
              <div className="billing-wrapper">
                <div className="left-billing-section">
                  <TextStyle weight="Bold">Pay now</TextStyle>
                </div>
                <div className="right-billing-section">
                  <TextStyle weight="Bold">
                    {locale &&
                      currency &&
                      tenure &&
                      new Intl.NumberFormat(locale, {
                        style: 'currency',
                        currency,
                        minimumFractionDigits: 2,
                        currencyDisplay: 'narrowSymbol'
                      }).format(planEstimate?.total)}
                  </TextStyle>
                </div>
              </div>
            )}
          </div>
          <div className="mt-20 flex jend">
            <ButtonGroup>
              <Button
                id="pricingContactSupport"
                plain={selectedPlan?.chargeBeePlanId !== 'solo'}
                primary={selectedPlan?.chargeBeePlanId === 'solo'}
                onClick={
                  selectedPlan?.chargeBeePlanId === 'solo'
                    ? () => onSoloPlanHandler()
                    : () => window.Intercom('show')
                }
              >
                <FormattedMessage id="app.pricing.contactsupport" />
              </Button>
              {selectedPlan?.chargeBeePlanId !== 'solo' && (
                <Button
                  id="pricingPaymentButton"
                  primary
                  onClick={openCheckout}
                >
                  <FormattedMessage id="app.pricing.payment.button" />
                </Button>
              )}
            </ButtonGroup>
          </div>
        </div>
      </InfoContainer>
    </PricingSummaryCardWrapper>
  );
};

export default withRouter(PricingSummaryCard);
